import React, { useEffect, useState, useRef } from 'react';
import { Minus, Plus, ChevronUp, ChevronDown, X, Maximize2, Settings } from 'lucide-react';
import { Dropdown } from '../ui/Dropdown';
import { TradeData } from '../../utils/krakenAPI';
interface OrderBookEntry {
  price: number;
  quantity: number;
  total: number;
}
interface OrderBookProps {
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
  spread?: number;
  spreadPercent?: number;
  trades?: TradeData[];
  height?: string;
  style?: React.CSSProperties;
}
export function OrderBook({
  bids,
  asks,
  spread,
  spreadPercent,
  trades = [],
  height = 'h-full',
  style
}: OrderBookProps) {
  const [grouping, setGrouping] = useState(0.1);
  const [activeView, setActiveView] = useState<'orderbook' | 'trades' | 'summary'>('orderbook');
  const [showGroupingMenu, setShowGroupingMenu] = useState(false);
  const asksRef = useRef<HTMLDivElement>(null);
  const groupingOptions = [0.01, 0.1, 1.0, 10.0];
  // Group orders by price level based on grouping setting
  const groupOrders = (orders: OrderBookEntry[], groupingSize: number): OrderBookEntry[] => {
    const grouped = new Map<number, {
      quantity: number;
      total: number;
    }>();
    orders.forEach(order => {
      const groupedPrice = Math.floor(order.price / groupingSize) * groupingSize;
      if (grouped.has(groupedPrice)) {
        const existing = grouped.get(groupedPrice)!;
        grouped.set(groupedPrice, {
          quantity: existing.quantity + order.quantity,
          total: existing.total + order.total
        });
      } else {
        grouped.set(groupedPrice, {
          quantity: order.quantity,
          total: order.total
        });
      }
    });
    return Array.from(grouped.entries()).map(([price, data]) => ({
      price,
      quantity: data.quantity,
      total: data.total
    }));
  };
  const rowsToShow = 15;
  // Asks: sorted so lowest ask is nearest the spread (bottom of asks section)
  // We want asks displayed high-to-low top-to-bottom, so lowest ask is at bottom near spread
  const groupedAsks = groupOrders(asks, grouping).sort((a, b) => a.price - b.price) // ascending: lowest first
  .slice(0, rowsToShow) // take the N lowest asks
  .sort((a, b) => b.price - a.price); // reverse to display highest at top, lowest at bottom (near spread)
  // Bids: sorted so highest bid is nearest the spread (top of bids section)
  const groupedBids = groupOrders(bids, grouping).sort((a, b) => b.price - a.price) // descending: highest first
  .slice(0, rowsToShow); // take the N highest bids
  // Calculate live spread from grouped data
  const lowestAsk = groupedAsks.length > 0 ? groupedAsks[groupedAsks.length - 1].price : undefined;
  const highestBid = groupedBids.length > 0 ? groupedBids[0].price : undefined;
  const liveSpread = lowestAsk !== undefined && highestBid !== undefined ? lowestAsk - highestBid : undefined;
  const liveSpreadPercent = liveSpread !== undefined && highestBid !== undefined && highestBid > 0 ? liveSpread / highestBid * 100 : undefined;
  const maxTotal = Math.max(...groupedBids.map(b => b.total), ...groupedAsks.map(a => a.total), 1);
  const formatNumber = (num: number, decimals: number = 2) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };
  const getPriceDecimals = (groupingSize: number): number => {
    if (groupingSize >= 10) return 0;
    if (groupingSize >= 1) return 1;
    if (groupingSize >= 0.1) return 1;
    return 2;
  };
  const priceDecimals = getPriceDecimals(grouping);
  const OrderRow = ({
    entry,
    type
  }: {
    entry: OrderBookEntry;
    type: 'bid' | 'ask';
  }) => {
    const depthPercent = entry.total / maxTotal * 100;
    const bgColor = type === 'bid' ? 'bg-[#00C87F]/10' : 'bg-[#E85D75]/10';
    return <div className="relative group hover:bg-background-elevated transition-colors" data-id="element-2066">
        {/* Depth bar */}
        <div className={`absolute inset-y-0 ${type === 'bid' ? 'right-0' : 'left-0'} ${bgColor} transition-all`} style={{
        width: `${depthPercent}%`
      }} data-id="element-2067" />

        {/* Content */}
        <div className="relative grid grid-cols-3 gap-2 px-3 py-1 text-xs font-mono" data-id="element-2068">
          <div className={`text-right ${type === 'bid' ? 'text-[#00C87F]' : 'text-[#E85D75]'}`} data-id="element-2069">
            {formatNumber(entry.price, priceDecimals)}
          </div>
          <div className="text-right text-text-primary" data-id="element-2070">
            {formatNumber(entry.quantity, 8)}
          </div>
          <div className="text-right text-text-secondary" data-id="element-2071">
            {formatNumber(entry.total)}
          </div>
        </div>
      </div>;
  };
  // Auto-scroll asks to bottom so spread stays visible
  useEffect(() => {
    requestAnimationFrame(() => {
      if (asksRef.current) {
        asksRef.current.scrollTop = asksRef.current.scrollHeight;
      }
    });
  }, [groupedAsks, grouping, activeView]);
  return <div className={`flex flex-col ${height}`} style={style} data-id="element-2072">
      {/* Header with Tabs */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-border-subtle" data-id="element-2073">
        <button onClick={() => setActiveView('orderbook')} className={`text-sm font-medium pb-1 -mb-[9px] transition-colors ${activeView === 'orderbook' ? 'text-text-primary border-b-2 border-brand-purple' : 'text-text-secondary hover:text-text-primary border-b-2 border-transparent'}`} data-id="element-2074">
          Order book
        </button>
        <button onClick={() => setActiveView('trades')} className={`text-sm font-medium pb-1 -mb-[9px] transition-colors ${activeView === 'trades' ? 'text-text-primary border-b-2 border-brand-purple' : 'text-text-secondary hover:text-text-primary border-b-2 border-transparent'}`} data-id="element-2075">
          Last trades
        </button>
      </div>

      {/* View Content */}
      {activeView === 'orderbook' && <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2076">
          {/* Grouping Controls */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle flex-shrink-0" data-id="element-2077">
            <div className="flex items-center gap-2" data-id="element-2078">
              <button onClick={() => {
            const currentIndex = groupingOptions.indexOf(grouping);
            if (currentIndex > 0) {
              setGrouping(groupingOptions[currentIndex - 1]);
            }
          }} disabled={grouping === groupingOptions[0]} className={`p-1.5 rounded transition-colors ${grouping === groupingOptions[0] ? 'text-text-tertiary cursor-not-allowed opacity-40' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated'}`} data-id="element-2079">
                <Minus size={14} data-id="element-2080" />
              </button>

              <div className="relative" data-id="element-2081">
                <button onClick={() => setShowGroupingMenu(!showGroupingMenu)} className="flex items-center gap-1 px-2 py-1 text-xs font-mono text-text-primary bg-background-elevated hover:bg-background-secondary rounded transition-colors" data-id="element-2082">
                  {grouping >= 1 ? grouping.toFixed(0) : grouping.toFixed(2)}
                  {showGroupingMenu ? <ChevronUp size={12} className="text-text-secondary" data-id="element-2083" /> : <ChevronDown size={12} className="text-text-secondary" data-id="element-2084" />}
                </button>

                {showGroupingMenu && <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowGroupingMenu(false)} data-id="element-2085" />
                    <div className="absolute left-0 top-full mt-1 bg-background-elevated border border-border-subtle rounded-lg shadow-lg py-1 z-20 min-w-[80px]" data-id="element-2086">
                      {groupingOptions.map(option => <button key={option} onClick={() => {
                  setGrouping(option);
                  setShowGroupingMenu(false);
                }} className={`w-full px-3 py-2 text-left text-xs font-mono transition-colors ${grouping === option ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary'}`} data-id="element-2087">
                          {option >= 1 ? option.toFixed(0) : option.toFixed(2)}
                        </button>)}
                    </div>
                  </>}
              </div>

              <button onClick={() => {
            const currentIndex = groupingOptions.indexOf(grouping);
            if (currentIndex < groupingOptions.length - 1) {
              setGrouping(groupingOptions[currentIndex + 1]);
            }
          }} disabled={grouping === groupingOptions[groupingOptions.length - 1]} className={`p-1.5 rounded transition-colors ${grouping === groupingOptions[groupingOptions.length - 1] ? 'text-text-tertiary cursor-not-allowed opacity-40' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated'}`} data-id="element-2088">
                <Plus size={14} data-id="element-2089" />
              </button>
            </div>
          </div>

          {/* Column Headers */}
          <div className="grid grid-cols-3 gap-2 px-3 py-2 text-[10px] text-text-tertiary uppercase tracking-wider border-b border-border-subtle font-medium flex-shrink-0" data-id="element-2090">
            <div className="text-right" data-id="element-2091">Price</div>
            <div className="text-right" data-id="element-2092">Quantity</div>
            <div className="text-right" data-id="element-2093">Total</div>
          </div>

          {/* Asks (Sell orders) - TOP HALF, red prices */}
          <div ref={asksRef} className="flex-1 basis-0 overflow-y-auto flex flex-col justify-end min-h-0" data-id="element-2094">
            {groupedAsks.length > 0 ? groupedAsks.map((ask, idx) => <OrderRow key={`ask-${idx}`} entry={ask} type="ask" data-id="element-2095" />) : <div className="flex items-center justify-center h-full text-text-tertiary text-xs" data-id="element-2096">
                Loading asks...
              </div>}
          </div>

          {/* Spread Indicator */}
          {liveSpread !== undefined && <div className="flex items-center justify-between px-3 py-2 bg-background-elevated border-y border-border-subtle flex-shrink-0" data-id="element-2097">
              <span className="text-xs text-text-secondary" data-id="element-2098">Spread</span>
              <div className="flex items-center gap-2" data-id="element-2099">
                <span className="text-xs font-mono text-text-primary" data-id="element-2100">
                  {formatNumber(liveSpread, priceDecimals)}
                </span>
                {liveSpreadPercent !== undefined && <span className="text-xs text-text-secondary" data-id="element-2101">
                    ({liveSpreadPercent.toFixed(2)}%)
                  </span>}
              </div>
            </div>}

          {/* Bids (Buy orders) - BOTTOM HALF, green prices */}
          <div className="flex-1 basis-0 overflow-y-auto min-h-0" data-id="element-2102">
            {groupedBids.length > 0 ? groupedBids.map((bid, idx) => <OrderRow key={`bid-${idx}`} entry={bid} type="bid" data-id="element-2103" />) : <div className="flex items-center justify-center h-full text-text-tertiary text-xs" data-id="element-2104">
                Loading bids...
              </div>}
          </div>
        </div>}

      {activeView === 'trades' && <>
          {/* Column Headers */}
          <div className="grid grid-cols-3 gap-2 px-3 py-2 text-[10px] text-text-tertiary uppercase tracking-wider border-b border-border-subtle font-medium" data-id="element-2105">
            <div className="text-right" data-id="element-2106">Price</div>
            <div className="text-right" data-id="element-2107">Quantity</div>
            <div className="text-right" data-id="element-2108">Total</div>
          </div>

          {/* Market Trades */}
          <div className="overflow-y-auto" data-id="element-2109">
            {trades.length > 0 ? trades.map((trade, idx) => {
          const tradeTime = new Date(trade.time);
          const timeString = tradeTime.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          return <div key={`${trade.time}-${idx}`} className="grid grid-cols-3 gap-2 px-3 py-1 text-xs font-mono hover:bg-background-elevated transition-colors" data-id="element-2110">
                    <div className={`text-right ${trade.side === 'sell' ? 'text-[#E85D75]' : 'text-[#00C87F]'}`} data-id="element-2111">
                      {trade.price.toLocaleString('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
              })}
                    </div>
                    <div className="text-right text-text-primary" data-id="element-2112">
                      {trade.volume.toFixed(8)}
                    </div>
                    <div className="text-right text-text-secondary" data-id="element-2113">
                      {timeString}
                    </div>
                  </div>;
        }) : <div className="flex items-center justify-center py-8 text-text-tertiary text-sm" data-id="element-2114">
                Loading trades...
              </div>}
          </div>
        </>}
    </div>;
}